import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Minus, Plus, ShoppingBasket, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { WHATSAPP_NUMBER, inr } from "../data/menu";

const buildMessage = (items, subtotal, form) => {
  const lines = items
    .map((it, i) => {
      const label = it.variant ? `${it.name} (${it.variant})` : `${it.name} (${it.unitLabel})`;
      return `${i + 1}. ${label} × ${it.qty} = ${inr(it.unitPrice * it.qty)}`;
    })
    .join("\n");
  return [
    "HARSHI'S KITCHEN — NEW ORDER",
    "",
    `Customer Name: ${form.name.trim()}`,
    `Phone: ${form.phone.trim()}`,
    `Delivery Address: ${form.address.trim()}`,
    "",
    "ORDER DETAILS",
    lines,
    "",
    `Food Subtotal: ${inr(subtotal)}`,
    "Delivery Charge: To be confirmed based on location",
    `Final Total: ${inr(subtotal)} + delivery charges (to be confirmed)`,
    "",
    `Special Instructions: ${form.instructions.trim() || "None"}`,
    "",
    "Please confirm this order and the delivery details.",
  ].join("\n");
};

export const CartDrawer = ({ open, onClose, items, onUpdateQty, onRemove, subtotal, count }) => {
  const [form, setForm] = useState({ name: "", phone: "", address: "", instructions: "" });
  const [errors, setErrors] = useState({});

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const placeOrder = () => {
    const er = {};
    if (!form.name.trim()) er.name = "Please enter your name";
    if (!/^[6-9]\d{9}$/.test(form.phone.replace(/[\s-]/g, ""))) er.phone = "Enter a valid 10-digit mobile number";
    if (form.address.trim().length < 8) er.address = "Please enter your full delivery address";
    setErrors(er);
    if (Object.keys(er).length) {
      toast.error("Please fill the required details");
      return;
    }
    const msg = buildMessage(items, subtotal, form);
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`, "_blank", "noopener");
    toast.success("WhatsApp opened", {
      description: "Review the order message and press send to confirm your order.",
    });
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-[#3D2314]/40 backdrop-blur-[2px] z-[60]"
          />
          <motion.aside
            data-testid="cart-drawer"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            className="fixed top-0 right-0 h-full w-full sm:w-[440px] bg-[#FFFDF8] z-[70] shadow-2xl flex flex-col"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#EFE6DC]">
              <h2 className="font-display text-xl font-semibold text-[#3D2314] flex items-center gap-2">
                <ShoppingBasket size={20} className="text-[#D86A7A]" /> Your Cart
                {count > 0 && <span className="text-sm font-sans text-[#8A7364] font-normal">({count} items)</span>}
              </h2>
              <button
                data-testid="cart-close-btn"
                onClick={onClose}
                aria-label="Close cart"
                className="w-9 h-9 rounded-full bg-[#FAF4EC] flex items-center justify-center text-[#5A4232] hover:bg-[#EFE6DC] transition-colors"
              >
                <X size={17} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4">
              {items.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center gap-3" data-testid="cart-empty-message">
                  <span className="w-16 h-16 rounded-full bg-[#FAF4EC] flex items-center justify-center text-[#D86A7A]">
                    <ShoppingBasket size={26} />
                  </span>
                  <p className="font-display text-lg text-[#3D2314]">Your cart is empty</p>
                  <p className="text-sm text-[#8A7364] max-w-[240px]">
                    Browse the menu and add some homemade goodness to get started.
                  </p>
                  <button
                    onClick={onClose}
                    className="mt-2 rounded-full bg-[#D86A7A] hover:bg-[#C85265] text-white px-6 py-2.5 text-sm font-semibold transition-colors"
                  >
                    Browse Menu
                  </button>
                </div>
              ) : (
                <>
                  <ul className="space-y-3">
                    {items.map((it) => (
                      <li
                        key={it.key}
                        data-testid={`cart-item-row-${it.key.replace(/[:\s]/g, "-")}`}
                        className="flex gap-3 bg-white rounded-2xl border border-[#EFE6DC] p-3"
                      >
                        <img src={it.img} alt={it.name} className="w-16 h-16 rounded-xl object-cover shrink-0" loading="lazy" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <p className="font-semibold text-[#3D2314] text-sm leading-tight">{it.name}</p>
                              <p className="text-xs text-[#8A7364] mt-0.5">
                                {it.variant ? it.variant : it.unitLabel} · {inr(it.unitPrice)}
                              </p>
                            </div>
                            <button
                              data-testid={`cart-remove-btn-${it.key.replace(/[:\s]/g, "-")}`}
                              onClick={() => onRemove(it.key)}
                              aria-label={`Remove ${it.name}`}
                              className="text-[#8A7364] hover:text-[#D86A7A] transition-colors p-1"
                            >
                              <Trash2 size={15} />
                            </button>
                          </div>
                          <div className="flex items-center justify-between mt-2">
                            <div className="flex items-center rounded-full border border-[#E8D8C8] bg-[#FAF4EC]">
                              <button
                                data-testid={`cart-decrease-btn-${it.key.replace(/[:\s]/g, "-")}`}
                                onClick={() => onUpdateQty(it.key, -1)}
                                className="w-7 h-7 flex items-center justify-center text-[#5A4232] hover:text-[#D86A7A]"
                                aria-label="Decrease quantity"
                              >
                                <Minus size={13} />
                              </button>
                              <span className="w-6 text-center text-xs font-bold text-[#3D2314]">{it.qty}</span>
                              <button
                                data-testid={`cart-increase-btn-${it.key.replace(/[:\s]/g, "-")}`}
                                onClick={() => onUpdateQty(it.key, 1)}
                                className="w-7 h-7 flex items-center justify-center text-[#5A4232] hover:text-[#D86A7A]"
                                aria-label="Increase quantity"
                              >
                                <Plus size={13} />
                              </button>
                            </div>
                            <span className="text-sm font-bold text-[#3D2314]">{inr(it.unitPrice * it.qty)}</span>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-5 bg-white rounded-2xl border border-[#EFE6DC] p-4 space-y-2 text-sm">
                    <div className="flex justify-between text-[#5A4232]">
                      <span>Food Subtotal</span>
                      <span data-testid="cart-subtotal" className="font-semibold text-[#3D2314]">{inr(subtotal)}</span>
                    </div>
                    <div className="flex justify-between text-[#5A4232]">
                      <span>Delivery Charges</span>
                      <span className="text-[#3A6B4C] font-medium text-xs sm:text-sm">To be confirmed</span>
                    </div>
                    <p className="text-[0.7rem] leading-snug text-[#8A7364]">
                      Delivery charges are extra and will be confirmed based on your location before the order is finalised.
                    </p>
                    <div className="border-t border-dashed border-[#EFE6DC] pt-2 flex justify-between items-center">
                      <span className="font-semibold text-[#3D2314]">Final Payable</span>
                      <span data-testid="cart-total" className="font-display text-lg font-bold text-[#D86A7A]">
                        {inr(subtotal)} <span className="text-[0.65rem] font-sans font-normal text-[#8A7364]">+ delivery</span>
                      </span>
                    </div>
                  </div>

                  <div className="mt-5 space-y-3">
                    <h3 className="font-display text-base font-semibold text-[#3D2314]">Delivery Details</h3>
                    {[
                      { k: "name", label: "Your Name", testid: "customer-name-input", type: "input", ph: "e.g. Harshita Modi" },
                      { k: "phone", label: "Phone Number", testid: "customer-phone-input", type: "input", ph: "10-digit mobile number" },
                      { k: "address", label: "Delivery Address", testid: "customer-address-input", type: "textarea", ph: "House no, street, area, landmark" },
                      { k: "instructions", label: "Special Instructions (optional)", testid: "customer-instructions-input", type: "textarea", ph: "Less spicy, no onion, etc." },
                    ].map((f) => (
                      <div key={f.k}>
                        <label className="text-xs font-semibold text-[#5A4232] mb-1 block">{f.label}</label>
                        {f.type === "input" ? (
                          <input
                            data-testid={f.testid}
                            value={form[f.k]}
                            onChange={set(f.k)}
                            placeholder={f.ph}
                            className={`w-full rounded-xl border bg-white px-3.5 py-2.5 text-sm text-[#3D2314] outline-none transition-colors placeholder:text-[#C4B3A4] ${
                              errors[f.k] ? "border-[#D86A7A]" : "border-[#E8D8C8] focus:border-[#D86A7A]"
                            }`}
                          />
                        ) : (
                          <textarea
                            data-testid={f.testid}
                            value={form[f.k]}
                            onChange={set(f.k)}
                            placeholder={f.ph}
                            rows={f.k === "address" ? 3 : 2}
                            className={`w-full rounded-xl border bg-white px-3.5 py-2.5 text-sm text-[#3D2314] outline-none transition-colors resize-none placeholder:text-[#C4B3A4] ${
                              errors[f.k] ? "border-[#D86A7A]" : "border-[#E8D8C8] focus:border-[#D86A7A]"
                            }`}
                          />
                        )}
                        {errors[f.k] && <p className="text-xs text-[#D86A7A] mt-1">{errors[f.k]}</p>}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            {items.length > 0 && (
              <div className="border-t border-[#EFE6DC] p-4 bg-[#FAF4EC]">
                <button
                  data-testid="whatsapp-place-order-btn"
                  onClick={placeOrder}
                  className="w-full rounded-full bg-[#3A6B4C] hover:bg-[#2F5A3F] text-white py-3.5 text-sm sm:text-base font-bold transition-all duration-300 active:scale-[0.98] shadow-[0_12px_28px_-8px_rgba(58,107,76,0.5)] flex items-center justify-center gap-2"
                >
                  Place Order on WhatsApp · {inr(subtotal)}
                </button>
                <p className="text-[0.7rem] text-center text-[#8A7364] mt-2">
                  Your order is confirmed only after you press send in WhatsApp.
                </p>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
